import * as React from "react";
import {
  Typography,
  Box,
  Button,
  Grid,
  MenuItem,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { connect } from "react-redux";
import {
  Navigate,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  MainInput,
  centered_flex_box,
  left_flex_box,
  main_button,
} from "../app/components/Styles";
import { isMobile } from "react-device-detect";
import { Image, notification, Space, Table, Tag } from "antd";
import Logo from "../app/components/logo.png";
import PropTypes from "prop-types";
import MuiPhoneNumber from "material-ui-phone-number";
import ImageComponent from "../app/components/ImageComponent";
import { addReturnScratch, getProducts, lookupBazaarPurchase } from "../app/store/actions/dataActions";
import { useEffect, useMemo } from "react";
import { AddCircleOutline, Search, Close } from "@mui/icons-material";
import moment from "moment";
import { useState } from "react";
import cities from "../constants/cities.json";
import { FincartAreaField } from "../app/components/FincartAreaField";
import { Chip, Paper } from "@mui/material";
import { useTranslation } from "react-i18next";

const hasValidPhone = (phone = "") => /^\+201\d{9}$/.test(phone.replace(/\s+/g, ""));
const MAX_REFUND_EGP = 4000;

export const ScratchReturnOrder = ({
  user,
  order,
  products,
  addReturnScratch,
  getProducts,
  lookupBazaarPurchase,
  isLoading
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [incomingItems, setIncomingItems] = useState([]);
  const [incomingItemsMap, setIncomingItemsMap] = useState(new Map()); // Map of _id -> product data
  const [refundAmount, setRefundAmount] = useState(0);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [formData, setFormData] = useState({
    customer: {
      first_name: "",
      last_name: "",
    },
    phone: "",
    shipping_address: {
      address1: "",
      address2: "",
      province: "Cairo",
      area: "",
    },
    shopifyOrderNumber: "",
    reason: ""
  });

  const [purchaseLookup, setPurchaseLookup] = useState('');

  const calculatedRefund = useMemo(() => {
    if (incomingItems.length === 0) return 0;
    return incomingItems.reduce((sum, item) => {
      const product = incomingItemsMap.get(item._id) || products?.docs?.find((p) => p._id === item._id);
      const price = parseFloat(product?.price) || 0;
      const quantity = parseInt(item.quantity, 10) || 0;
      return sum + (price * quantity);
    }, 0);
  }, [incomingItems, incomingItemsMap, products]);

  useEffect(() => {
    setRefundAmount(calculatedRefund);
  }, [calculatedRefund]);

  const parsedRefundAmount = parseFloat(refundAmount) || 0;
  const exceedsMaxRefund = parsedRefundAmount > MAX_REFUND_EGP;

  const handlePurchaseLookup = () => {
    if (!purchaseLookup.trim()) return;
    lookupBazaarPurchase(purchaseLookup.trim()).then((data) => {
      const { prefill } = data;
      setFormData((prev) => ({
        ...prev,
        customer: prefill.customer,
        phone: prefill.phone || prev.phone,
        shopifyOrderNumber: data.purchase.purchaseNumber,
      }));
      const items = prefill.items.map((item) => ({
        _id: item._id,
        quantity: item.quantity,
      }));
      setIncomingItems(items);
      const map = new Map();
      prefill.items.forEach((item) => map.set(item._id, item));
      setIncomingItemsMap(map);
      notification.success({ message: t('bazaars.lookupPurchase') });
    });
  };

  const canSubmit = () => {
    return (
      formData.customer.first_name &&
      formData.customer.last_name &&
      hasValidPhone(formData.phone) &&
      formData.reason &&
      formData.shipping_address.address1 &&
      formData.shipping_address.province &&
      incomingItems.length > 0 &&
      !exceedsMaxRefund
    );
  };

  useEffect(() => {
    const params = {
      page,
      limit: pageSize,
      search: search || undefined,
    };
    getProducts(params);
  }, [page, pageSize]);

  const handleSearch = () => {
    setPage(1);
    const params = {
      page: 1,
      limit: pageSize,
      search: search || undefined,
    };
    getProducts(params);
  };

  const handleClearSearch = () => {
    setSearch('');
    setPage(1);
    const params = {
      page: 1,
      limit: pageSize,
    };
    getProducts(params);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const handleSubmit = () => {
    if (exceedsMaxRefund) {
      notification.error({
        message: t("returnOrder.maxRefundExceeded", { max: MAX_REFUND_EGP }),
      });
      return;
    }
    if(!canSubmit()) {
      notification.error({
        message: hasValidPhone(formData.phone)
          ? t("fulfillment.errors.requiredFields")
          : t("fulfillment.errors.phoneFormat"),
      });
      return;
    }
    addReturnScratch({ orderItems: incomingItems, refundAmount: parsedRefundAmount, ...formData }, navigate)
  }

  const returnColumns = [
    {
      title: t("common.sku"),
      dataIndex: 'externalId',
    },
    // {
    //   title: 'Name',
    //   dataIndex: 'name',
    // },
    {
      title: t("common.price"),
      dataIndex: 'price',
      render: (text) => `${text} EGP`,
    },
    {
      title: t("common.size"),
      dataIndex: 'size',
    },
    {
      title: t("returnOrder.qtyToReturn"),
      render: (_, { _id, quantityOrdered }) => <Space>
        <MainInput
          margin="normal"
          type="number"
          fullWidth
          id={_id}
          disabled={incomingItems.filter((item) => item._id === _id).length === 0}
          inputProps={{
            min: incomingItems.filter((item) => item._id === _id).length > 0 ? 1 : 0,
            max: quantityOrdered
          }}
          value={incomingItems.filter((item) => item._id === _id).length > 0 ? incomingItems.filter((item) => item._id === _id)[0].quantity : 0}
          onChange={({ target }) => onQuantityChange(_id, target.value)}
        />
      </Space>
    },
  ];

  const returnData = products?.docs?.map((row) => {
    return {
      _id: row._id,
      key: row._id,
      externalId: row.sku || row.externalId,
      price: row.price,
      image: row.image,
      name: row.name,
      size: row.size,
      color: row.color,
      weight: row.weight,
    }
  })

  const onQuantityChange = (_id, newQuantity) => {
    const newItems = incomingItems.map((item) => {
      if (item._id === _id) {
        return { ...item, quantity: parseInt(newQuantity) || 1 };
      }
      return item
    });
    setIncomingItems(newItems);
  };

  const removeIncomingItem = (_id) => {
    setIncomingItems(incomingItems.filter(item => item._id !== _id));
    const newMap = new Map(incomingItemsMap);
    newMap.delete(_id);
    setIncomingItemsMap(newMap);
  };

  // Get current page's row IDs
  const currentPageIncomingIds = returnData?.map(row => row._id) || [];

  const onSelectChange = (newSelectedRowKeys) => {
    // Get current selections (excluding items from current page)
    const existingSelections = incomingItems.filter(item => !currentPageIncomingIds.includes(item._id));
    
    // Get newly selected items from current page
    const newSelections = newSelectedRowKeys
      .filter(key => currentPageIncomingIds.includes(key))
      .map((key) => {
        const existing = incomingItems.find(item => item._id === key);
        return existing || { _id: key, quantity: 1 };
      });
    
    // Merge: keep existing + new/updated selections
    const merged = [...existingSelections, ...newSelections];
    
    // Update product map for display
    const newMap = new Map(incomingItemsMap);
    products?.docs?.forEach(product => {
      if (newSelectedRowKeys.includes(product._id)) {
        newMap.set(product._id, product);
      } else if (currentPageIncomingIds.includes(product._id)) {
        newMap.delete(product._id);
      }
    });
    
    setIncomingItems(merged);
    setIncomingItemsMap(newMap);
  };

  const rowSelection = {
    selectedRowKeys: incomingItems
      .filter(item => currentPageIncomingIds.includes(item._id))
      .map(item => item._id),
    onChange: onSelectChange,
  };

  return (
    <div>
      <Box sx={{ width: "100%", padding: 5, mt: 2 }}>
        <Box sx={{ ...centered_flex_box, mb: 1 }}>
          <Typography variant='h4' sx={{ color: "var(--secColor4)" }}>{t("returns.addExternalReturn")}</Typography>
          {/* <IconButton onClick={() => navigate("/ims/admin/products/add")} sx={{ color: "var(--secColor)", mx: 2 }}><AddCircleOutline /></IconButton> */}
        </Box>
        <Box sx={{}}>
          <Box sx={{ ...left_flex_box }}>
            <Grid container sx={{}}>
              <Grid item xs={12}>
                <Typography variant='h6'>{t('bazaars.lookupPurchase')}</Typography>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} md={8}>
                    <MainInput
                      margin="normal"
                      label={t('bazaars.purchaseNumber')}
                      placeholder="#POS-BRAND-1001"
                      value={purchaseLookup}
                      fullWidth
                      onChange={({ target }) => setPurchaseLookup(target.value)}
                      onKeyPress={(e) => { if (e.key === 'Enter') handlePurchaseLookup(); }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Button sx={main_button} onClick={handlePurchaseLookup}>{t('common.search')}</Button>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Typography variant='h6'>{t("fulfillment.customerInformation")}</Typography>
                <Grid item container spacing={2} xs={12}>
                  <Grid item xs={4}>
                    <MainInput
                      margin="normal"
                      label={t("fulfillment.firstName")}
                      required
                      value={formData.customer.first_name}
                      fullWidth
                      onChange={({ target }) => setFormData({ ...formData, customer: { ...formData.customer, first_name: target.value } })}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <MainInput
                      margin="normal"
                      label={t("fulfillment.lastName")}
                      required
                      value={formData.customer.last_name}
                      fullWidth
                      onChange={({ target }) => setFormData({ ...formData, customer: { ...formData.customer, last_name: target.value } })}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <MuiPhoneNumber
                      sx={{
                        "& label": {
                          color: "var(--secColor4) !important",
                        },
                        "& label.Mui-focused": {
                          color: "var(--secColor4) !important",
                        },
                        "& .MuiInput-underline:after": {
                          borderBottomColor: "green",
                        },
                        "& .MuiInputBase-input": {
                          outerHeight: 500,
                          color: "var(--secColor4)",
                        },
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            border: "1px solid var(--secColor4)",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "var(--terColor)",
                          },

                          borderRadius: 0,
                        }
                      }}
                      margin="normal"
                      fullWidth
                      required
                      label={t("fulfillment.phoneNumber")}
                      variant='outlined'
                      onlyCountries={['eg']}
                      defaultCountry={'eg'}
                      countryCodeEditable={false}
                      inputProps={{
                        minLength: 13
                      }}
                      disableAreaCodes
                      value={formData.phone}
                      onChange={(value) => setFormData({ ...formData, phone: value })}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Typography variant='h6'>{t("fulfillment.shippingInformation")}</Typography>
                <Grid item container spacing={2} xs={12}>
                  <Grid item xs={5}>
                    <MainInput
                      margin="normal"
                      label={t("orderDetails.addressLine1")}
                      required
                      value={formData.shipping_address.address1}
                      fullWidth
                      onChange={({ target }) => setFormData({ ...formData, shipping_address: { ...formData.shipping_address, address1: target.value } })}
                    />
                  </Grid>
                  <Grid item xs={5}>
                    <MainInput
                      margin="normal"
                      label={t("orderDetails.addressLine2")}
                      value={formData.shipping_address.address2}
                      fullWidth
                      onChange={({ target }) => setFormData({ ...formData, shipping_address: { ...formData.shipping_address, address2: target.value } })}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <MainInput
                      margin="normal"
                      label={t("fulfillment.province")}
                      required
                      value={formData.shipping_address.province}
                      fullWidth
                      select
                      onChange={({ target }) => setFormData({ ...formData, shipping_address: { ...formData.shipping_address, province: target.value, area: "" } })}
                    >
                      {cities.map((city) => <MenuItem key={city} value={city}>{city}</MenuItem>)}
                    </MainInput>
                  </Grid>
                  {user?.vendor?.shippingService === "Fincart" && (
                    <Grid item xs={12}>
                      <FincartAreaField
                        shippingService={user?.vendor?.shippingService}
                        city={formData.shipping_address.province}
                        area={formData.shipping_address.area}
                        onChange={({ target }) => setFormData({ ...formData, shipping_address: { ...formData.shipping_address, area: target.value } })}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Typography variant='h6'>{t("orderDetails.orderInformation")}</Typography>
                <Grid item container spacing={2} xs={12}>
                  <Grid item xs={4}>
                    <MainInput
                      margin="normal"
                      label={t("orderDetails.shopifyOrderNumber")}
                      placeholder="#XXXX"
                      value={formData.shopifyOrderNumber}
                      fullWidth
                      onChange={({ target }) => setFormData({ ...formData, shopifyOrderNumber: target.value })}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <MainInput
                      margin="normal"
                      label={t("returns.returnReason")}
                      required
                      value={formData.reason}
                      fullWidth
                      select
                      onChange={({ target }) => setFormData({ ...formData, reason: target.value })}
                    >
                      <MenuItem value={"Defective Items"}>{t("home.defectiveItems")}</MenuItem>
                      <MenuItem value={"Customer Errors"}>{t("home.customerIssues")}</MenuItem>
                      <MenuItem value={"Warehouse Mistake"}>{t("home.wrongItems")}</MenuItem>
                    </MainInput>
                  </Grid>
                  <Grid item xs={4}>
                    <MainInput
                      margin="normal"
                      type="number"
                      label={t("returnOrder.refundAmountEGP")}
                      value={refundAmount}
                      fullWidth
                      error={exceedsMaxRefund}
                      helperText={exceedsMaxRefund
                        ? t("returnOrder.maxRefundExceeded", { max: MAX_REFUND_EGP })
                        : t("returnOrder.refundAutoCalculated")}
                      inputProps={{ min: 0, max: MAX_REFUND_EGP }}
                      onChange={({ target }) => setRefundAmount(target.value)}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Button sx={{ ...main_button }} onClick={handleSubmit} loading={isLoading}>
                  {t("returnOrder.sendReturn")}
                </Button>
              </Grid>
            </Grid>
          </Box>
          
          {/* Selected Items Display */}
          {incomingItems.length > 0 && (
            <Box sx={{ mb: 3, mt: 2 }}>
              <Paper sx={{ p: 2, backgroundColor: 'var(--cardBg)', border: '1px solid var(--borderColor)' }}>
                <Typography variant='h6' sx={{ mb: 2, color: "var(--secColor4)" }}>{t("duplicateOrder.selectedItems", { count: incomingItems.length })}</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {incomingItems.map((item) => {
                    const product = incomingItemsMap.get(item._id) || products?.docs?.find(p => p._id === item._id);
                    if (!product) return null;
                    return (
                      <Chip
                        key={item._id}
                        label={`${product.sku || product.externalId} (${t("orderDetails.quantity")}: ${item.quantity})${product.size ? ` - ${product.size}` : ''}`}
                        onDelete={() => removeIncomingItem(item._id)}
                        deleteIcon={<Close />}
                        sx={{ 
                          backgroundColor: 'var(--tableRowBg)', 
                          border: '1px solid var(--borderColor)',
                          color: 'var(--textPrimary)',
                          '& .MuiChip-deleteIcon': {
                            color: 'var(--textSecondary)',
                            '&:hover': {
                              color: 'var(--primaryColor)'
                            }
                          }
                        }}
                      />
                    );
                  })}
                </Box>
              </Paper>
            </Box>
          )}
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography textAlign={"center"}>{t("exchangeOrder.incomingItemsTitle")}</Typography>
              
              {/* Search Bar */}
              <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                <MainInput
                  margin="normal"
                  placeholder={t("returnsSystem.manageProducts.searchPlaceholder")}
                  value={search}
                  onChange={({ target }) => setSearch(target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {search && (
                          <IconButton
                            onClick={handleClearSearch}
                            edge="end"
                            sx={{ color: "var(--secColor4)" }}
                          >
                            ×
                          </IconButton>
                        )}
                        <IconButton
                          onClick={handleSearch}
                          edge="end"
                          sx={{ color: "var(--secColor4)" }}
                        >
                          <Search />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: '100%', maxWidth: 400 }}
                />
              </Box>
              
              <Table
                columns={returnColumns}
                dataSource={returnData}
                showSorterTooltip={{
                  target: 'sorter-icon',
                }}
                loading={isLoading}
                rowSelection={rowSelection}
                pagination={{
                  current: page,
                  pageSize: pageSize,
                  total: products?.totalDocs,
                  showSizeChanger: true,
                  showQuickJumper: false,
                  showTotal: (total) => `${t('table.total')} ${total} ${t('table.items')}`,
                }}
                onChange={handleTableChange}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state?.auth?.user,
  isLoading: state?.records?.isLoading || state?.wait?.isLoading,
  products: state?.records?.products,
  token: state?.auth?.token,
  order: state?.records?.order
});

const mapDispatchToProps = { getProducts, addReturnScratch, lookupBazaarPurchase };

export default connect(mapStateToProps, mapDispatchToProps)(ScratchReturnOrder);
